import { useEffect, useState } from 'preact/hooks'
import { websApi, type WebViewDto, type WebTopLevelPatchDto, type FileImportSummaryDto } from '@/api/webs'
import { isValidSlugFormat, WEB_SLUG_MAX_LENGTH } from '@/utils/slugify'
import { audienceApi } from '@/api/audience'
import type { CampaignFileImportListDto, AudienceSearchResultDto } from '@/types/audience'
import { useToast } from '@/hooks/useToast'
import { useLookups } from '@/hooks/useLookups'
import { useLandingPage } from '@/hooks/useLandingPage'
import { useUserProfile } from '@/hooks/useUserProfile'
import { Pencil, Save, X, Trash2, Settings } from 'lucide-preact'
import { TextBoxField, DatePicker, NumberInput } from '@/components'
import { Select } from '@/components/common/Select'
import { SlugInput } from '@/components/common/SlugInput'
import type { SelectOption } from '@/components/common/Select'
import { setFormValidationErrors } from '@/utils/errorHandling'
import { marketingNeedsConference } from '@/utils/onlineEventMode'
import { CampaignListModal } from '@/components/campaigns/campaignList/CampaignListModal'
import { DeleteWebModal } from '@/components/webs/modals/DeleteWebModal'
import { useAIForm } from '@/hooks'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'

interface Props {
  web: WebViewDto
  onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
  clientCompanyId: string
  workspaceId: string
  campaignId: string
}

type TopLevelForm = { Name: string; Slug: string; Status: string; LaunchDate: string; LeadGoal: string; Description?: string; IncludedFileImports: FileImportSummaryDto[] }

export function TopLevelEditSection({ web, onPatchWeb, clientCompanyId, workspaceId, campaignId }: Props) {
  const { showError, showSuccess, showErrorUnless422 } = useToast()
  const { getWebStatuses } = useLookups()
  // ORB-306: web mutations require Workspace Editor — gate the edit/delete affordances
  const { getWorkspaceCapabilities } = useUserProfile()
  const { canEdit } = getWorkspaceCapabilities(clientCompanyId, workspaceId)

  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  /**
   * The slug is immutable while a landing page is published (ORB-461). The API is the enforcement
   * point; this only mirrors it so the field is visibly locked instead of failing on save.
   *
   * Derived here rather than read off WebViewDto: computing it server-side needs a landing-page read
   * the static WebViewMapper cannot do, and threading it through all nineteen functions that build a
   * WebViewDto means nineteen new dependencies or a flag that lies on most paths. Same call as ORB-456.
   *
   * Fails closed. `landingPage` starts null, so keying the lock off it alone leaves the field editable
   * until the fetch resolves — loading on mount narrows that window but does not close it, and a user
   * who opens Edit quickly on a published page could still type into it. The slug stays locked until
   * the publish state is actually known.
   */
  const { landingPage, error: landingPageError, load: loadLandingPage } = useLandingPage({ clientCompanyId, workspaceId, campaignId, webId: web.Id })
  const [loadSettled, setLoadSettled] = useState(false)
  useEffect(() => {
    let active = true
    loadLandingPage().then(() => { if (active) setLoadSettled(true) })
    return () => { active = false }
  }, [loadLandingPage])

  // Both conditions are needed. load() resolves rather than throws on failure and returns null for a
  // 404 as well as for an error, so settling alone does not mean the publish state is known — without
  // the error check a failed request would read as "no landing page" and unlock the field.
  const publishStateKnown = loadSettled && !landingPageError

  /**
   * Sign-off is the earlier and stricter of the two freeze points (ORB-482): it pushes the slug to
   * BigMarker as the event's sub_url, and nothing can push a correction afterwards, so a change here
   * would leave the platform URL and the event URL permanently diverged.
   *
   * Read off ApprovedAt rather than ApprovalStatus for the same reason the API does: a reopen resets
   * the status but deliberately preserves ApprovedAt (ORB-462), and the slug is not handed back on a
   * reopen. The publish lock stays alongside it because deploy gates on the asset version's sign-off
   * and not the Web's, so a web can be published having never been signed off.
   */
  const slugFrozenBySignOff = !!web.ApprovedAt
  const slugLocked = slugFrozenBySignOff || !publishStateKnown || !!landingPage?.IsPublished
  const slugLockedMessage = slugFrozenBySignOff
    ? 'The public address is fixed once a web has been signed off.'
    : landingPageError
      ? 'Could not confirm whether the landing page is published, so the slug stays locked. Reload to try again.'
      : !loadSettled
        ? 'Checking whether the landing page is published…'
        : 'Unpublish the landing page to rename the slug.'

  /** The public title, whichever sub-entity holds it. Undefined until one is set. */
  const publicTitle = web.OnlineEvent?.Title || web.Download?.Title || web.Meeting?.Title || web.Demo?.Title || undefined
  const [audiences, setAudiences] = useState<CampaignFileImportListDto[]>([])
  const [showAudienceModal, setShowAudienceModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const { form } = useAIForm<TopLevelForm>({
    mode: 'onChange',
    defaultValues: { Name: web.Name || '', Slug: web.Slug || '', Status: web.Status || '', LaunchDate: web.LaunchDate || '', LeadGoal: String(web.LeadGoal ?? 0), Description: web.Description || '', IncludedFileImports: web.IncludedFileImports || [] }
  }, {
    aiContext: { campaignId, webId: web.Id, location: ChatLocations.WebEditor, entityType: ChatEntityTypes.Web, entityId: web.Id, viewMode: editing ? ChatViewModes.Edit : ChatViewModes.View },
    fields: [
      { aiName: 'webName', formPath: 'Name', displayName: 'Web Name', fieldType: 'text', maxLength: 200 },
      { aiName: 'webDescription', formPath: 'Description', displayName: 'Description', fieldType: 'textarea', maxLength: 5000 },
    ],
    isEditMode: true,
    onFieldUpdate: () => {
      if (!editing) setEditing(true)
    },
  })
  const { register, control, watch, reset, handleSubmit, formState, setError, clearErrors, setValue } = form
  const { errors, isDirty } = formState

  const marketingLocked = marketingNeedsConference(web)

  const statusOptions: SelectOption[] = getWebStatuses().map(status => ({
    value: status.Key,
    label: status.Description,
    disabled: marketingLocked && status.Key === 'Marketing',
  }))

  // Load audiences
  useEffect(() => { (async () => {
    if (!campaignId) return;
    const res = await audienceApi.listByCampaign(clientCompanyId, workspaceId, campaignId);
    if (res.data) setAudiences(res.data)
  })() }, [campaignId, workspaceId])

  const handleAudienceCreated = (result: AudienceSearchResultDto) => {
    showSuccess('Audience created', `Audience "${result.FileName || result.CampaignName || 'Audience'}" created.`)
    setShowAudienceModal(false)
    ; (async () => { const res = await audienceApi.listByCampaign(clientCompanyId, workspaceId, campaignId); if (res.data) setAudiences(res.data) })()
  }

  // Reset when entering edit mode
  useEffect(() => { if (editing) { reset({ Name: web.Name || '', Slug: web.Slug || '', Status: web.Status || '', LaunchDate: web.LaunchDate || '', LeadGoal: String(web.LeadGoal ?? 0), Description: web.Description || '', IncludedFileImports: web.IncludedFileImports || [] }) } }, [editing, web.Name, web.Slug, web.Status, web.LaunchDate, web.LeadGoal, web.Description, web.IncludedFileImports])

  // Live validation for audience rules
  const watchedStatus = watch('Status')
  const watchedAudienceImports = (watch('IncludedFileImports') || []) as FileImportSummaryDto[]
  const watchedAudienceIds = watchedAudienceImports.map(fi => fi.Id)
  useEffect(() => {
    if (!editing) return
    const prevError = errors.IncludedFileImports
    const previousAudienceIds = (web.IncludedFileImports || []).map(fi => fi.Id)
    const submissionCount = (web as unknown as { SubmissionCount?: number }).SubmissionCount ?? web.LeadCount ?? 0
    const hasSubmissions = submissionCount > 0
    // Marketing status requires at least one audience
    const requiresAudience = watchedStatus === 'Marketing'
    // Once submissions exist, prevent audience removal for these statuses
    const restrictedStatuses = ['In Planning', 'Asset Creation', 'Marketing', 'Completed', 'Archived']
    const removalAttempted = previousAudienceIds.some(id => !watchedAudienceIds.includes(id))
    if (requiresAudience && watchedAudienceIds.length === 0) {
      if (!prevError || prevError.message !== 'At least one audience is required for Marketing status.') setError('IncludedFileImports', { type: 'manual', message: 'At least one audience is required for Marketing status.' })
      return
    }
    if (removalAttempted && hasSubmissions && restrictedStatuses.includes(web.Status || '')) {
      if (!prevError || prevError.message !== 'You cannot remove audiences after submissions have occurred.') setError('IncludedFileImports', { type: 'manual', message: 'You cannot remove audiences after submissions have occurred.' })
      return
    }
    if (prevError) clearErrors('IncludedFileImports')
  }, [editing, watchedStatus, watchedAudienceImports, web.IncludedFileImports, web.Status, errors.IncludedFileImports, setError, clearErrors])

  const submit = handleSubmit(async (vals) => {
    setSaving(true)
    const leadGoalParsed = (() => { if (!vals.LeadGoal || vals.LeadGoal.trim() === '') return 0; const n = Number(vals.LeadGoal); if (Number.isNaN(n) || n < 0) return 0; return Math.floor(n) })()
    const includedIds = (vals.IncludedFileImports || []).map(fi => fi.Id)
    const slugChanged = vals.Slug !== (web.Slug || '')
    if (slugChanged && vals.Slug.trim() && !isValidSlugFormat(vals.Slug.trim(), WEB_SLUG_MAX_LENGTH)) { setError('Slug' as never, { type: 'manual', message: `Slug must be 3–${WEB_SLUG_MAX_LENGTH} lowercase letters, numbers, or hyphens.` }); showError('Validation error', 'Invalid slug format.'); setSaving(false); return }
    const dto: WebTopLevelPatchDto = { ETag: web.ETag, Name: vals.Name.trim(), Slug: slugChanged ? (vals.Slug.trim() || null) : undefined, Status: vals.Status.trim(), LaunchDate: vals.LaunchDate, LeadGoal: leadGoalParsed, Description: vals.Description?.trim() ? vals.Description.trim() : undefined, IncludedFileImportIds: includedIds.length > 0 ? includedIds : [] }
    clearErrors('IncludedFileImports')
    // Belt to the disabled option's braces. The option can't be clicked, but this is the check that
    // actually protects the rest of the form: the API rejects the whole PATCH on a bad status, so
    // anything that slipped Marketing into the form would take the slug and every other field down
    // with it (ORB-478). Same condition as the server's transition rule.
    if (dto.Status === 'Marketing' && marketingLocked) {
      // Conditional on the integration, matching the server message. Sign-off creates the conference
      // only when BigMarker is connected, so an unconditional promise would be wrong for a workspace
      // without one and would send them round the loop again (ORB-478).
      setError('Status' as never, { type: 'manual', message: 'Sign this web off first, with BigMarker connected.' })
      showError('Cannot activate yet', 'This web has no webinar yet. Signing it off creates one, provided BigMarker is connected. Then set the status to Marketing.')
      setSaving(false)
      return
    }
    const audienceErrors: string[] = []
    if (dto.Status === 'Marketing' && includedIds.length === 0) audienceErrors.push('At least one audience is required for Marketing status.')
    const previousAudienceIds = (web.IncludedFileImports || []).map(fi => fi.Id)
    const removedAudienceIds = previousAudienceIds.filter(id => !includedIds.includes(id))
    if (removedAudienceIds.length > 0) {
      const restrictedStatuses = ['In Planning', 'Asset Creation', 'Marketing', 'Completed', 'Archived']
      const submissionCount: number = (web as unknown as { SubmissionCount?: number }).SubmissionCount ?? web.LeadCount ?? 0
      if (restrictedStatuses.includes(web.Status || '') && submissionCount > 0) audienceErrors.push('You cannot remove audiences after submissions have occurred.')
    }
    if (audienceErrors.length > 0) { setError('IncludedFileImports', { type: 'manual', message: audienceErrors[0] }); showError('Validation error', audienceErrors[0]); setSaving(false); return }
    const res = await websApi.patchTopLevel(web.WorkspaceId, web.Id, dto)
    if (!res.error && res.data) {
      onPatchWeb(prev => ({ ...prev, ...res.data }))
      showSuccess('Updated', 'Top-level details saved.')
      setEditing(false)
    } else {
      if (res.status === 422) setFormValidationErrors(res, setError)
      else showErrorUnless422('Save failed', res, 'Unable to update web')
    }
    setSaving(false)
  })

  const hasLeads = (web.LeadCount || 0) > 0

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="relative inline-flex">
            <Settings className="w-5 h-5 text-primary-600 dark:text-primary-500" />
            <span className="absolute -inset-1 rounded-full opacity-0 group-hover:opacity-70 bg-primary-500/10 blur-sm transition" />
          </span>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">Web Settings</h2>
        </div>
        {canEdit && (!editing ? (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"><Pencil className="w-4 h-4" /> Edit</button>
            {!hasLeads && (<button type="button" onClick={() => setShowDeleteModal(true)} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="w-4 h-4" /> Delete</button>)}
            {hasLeads && (<button type="button" onClick={() => setShowDeleteModal(true)} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border border-amber-500 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20">Archive</button>)}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button type="button" onClick={submit} disabled={saving || !isDirty || Object.keys(errors).length > 0} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">{saving ? 'Saving...' : (<><Save className="w-4 h-4" /> Save</>)}</button>
            <button type="button" onClick={() => { setEditing(false); reset() }} disabled={saving} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"><X className="w-4 h-4" /> Cancel</button>
          </div>
        ))}
      </div>
      {!editing ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
          <div className="space-y-2">
            <div><span className="font-semibold text-slate-500 dark:text-slate-400">Name:</span> <span className="text-slate-800 dark:text-slate-200 text-sm">{web.Name || '—'}</span></div>
            {web.Slug && <div><span className="font-semibold text-slate-500 dark:text-slate-400">Slug:</span> <span className="text-slate-800 dark:text-slate-200 text-sm font-mono">{web.Slug}</span></div>}
            <div><span className="font-semibold text-slate-500 dark:text-slate-400">Status:</span> <span className="text-slate-800 dark:text-slate-200 text-sm">{web.Status}</span></div>
            <div><span className="font-semibold text-slate-500 dark:text-slate-400">Launch Date:</span> <span className="text-slate-800 dark:text-slate-200 text-sm">{web.LaunchDate ? (() => { const [y, m, d] = web.LaunchDate.split('-'); return `${m}/${d}/${y}` })() : '—'}</span></div>
            <div><span className="font-semibold text-slate-500 dark:text-slate-400">Lead Goal:</span> <span className="text-slate-800 dark:text-slate-200 text-sm">{web.LeadGoal?.toLocaleString?.() || '0'}</span></div>
          </div>
          <div className="space-y-2">
            <div><span className="font-semibold text-slate-500 dark:text-slate-400">Audiences:</span> {web.IncludedFileImports && web.IncludedFileImports.length > 0 ? (
              <span className="inline-flex flex-wrap gap-1">{web.IncludedFileImports.map(fi => (
                <span key={fi.Id} className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 text-[11px]">{fi.Name}</span>
              ))}</span>
            ) : <span className="text-slate-400">None</span>}</div>
          </div>
          {web.Description && (
            <div className="md:col-span-2"><span className="font-semibold text-slate-500 dark:text-slate-400">About:</span> <span className="text-slate-700 dark:text-slate-300 whitespace-pre-line text-sm">{web.Description}</span></div>
          )}
        </div>
      ) : (
        <form className="space-y-4" onSubmit={e => { e.preventDefault(); submit() }}>
          <TextBoxField
            control={control}
            name="Name"
            labelSize="xs"
            label="Web Name"
            required
            showCharCounter
            maxLength={200}
            placeholder="Name"
            rules={{ required: 'Name is required', maxLength: { value: 200, message: 'Max 200 characters' }, validate: v => (!!v?.trim()) || 'Name cannot be empty' }}
          />
          <SlugInput
            label="Slug"
            maxLength={WEB_SLUG_MAX_LENGTH}
            fitSuggestion
            value={watch('Slug') || ''}
            onChange={(v) => setValue('Slug', v, { shouldValidate: true, shouldDirty: true })}
            // Seeded from the public title, never from Name. Name is an internal planning code
            // (ORB-459) and the slug is the public URL segment. Undefined until a title exists, which
            // leaves auto-fill switched off rather than seeding it from the wrong field.
            sourceName={!web.Slug ? publicTitle : undefined}
            locked={slugLocked}
            lockedMessage={slugLockedMessage}
            // Nothing is stored for this web yet, so the auto-filled value is a suggestion. Saying so
            // is what stops the field contradicting the sign-off readiness chip (ORB-482).
            unsaved={!web.Slug}
            checkAvailability={async (slug) => {
                if (slug === web.Slug) return { available: true }
                const res = await websApi.checkSlug(workspaceId, slug)
                return res.data ?? { available: false }
            }}
            helperText={!web.Slug
              ? 'Suggested from the event title. Save to claim it, or leave it and you will be asked when you sign off.'
              : 'URL segment for the deployed landing page.'}
          />
          <div className="flex flex-wrap gap-4">
            <Select
              labelSize="xs"
              label="Status"
              required
              options={statusOptions}
              placeholder="Select"
              containerClassName="w-44"
              error={errors.Status?.message}
              helperText={marketingLocked ? 'Marketing unlocks once this web has a webinar, which sign-off creates when BigMarker is connected.' : undefined}
              {...register('Status', { required: 'Status is required' })}
            />
            <DatePicker
              labelSize="xs"
              label="Launch Date"
              required
              containerClassName="w-full max-w-xs"
              error={errors.LaunchDate?.message}
              {...register('LaunchDate', { required: 'Launch Date is required' })}
            />
            <NumberInput
              labelSize="xs"
              label="Lead Goal"
              min={0}
              placeholder="0"
              containerClassName="w-44"
              error={errors.LeadGoal?.message ? String(errors.LeadGoal.message) : undefined}
              {...register('LeadGoal', { validate: (v) => { if (!v || v.trim() === '') return true; const n = Number(v); if (Number.isNaN(n)) return 'Must be a number'; if (!Number.isInteger(n)) return 'Must be an integer'; if (n < 0) return 'Must be >= 0'; if (n > 100000000) return 'Too large'; return true } })}
            />
            <div className="w-full max-w-xs">
              <div className="flex items-center justify-between mb-1"><label className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Audiences{watch('Status') === 'Marketing' && ' *'}</label><button type="button" onClick={() => setShowAudienceModal(true)} className="text-xs text-primary-600 dark:text-primary-400 hover:underline font-medium">+ Create New</button></div>
              <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-1 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700">
                {audiences.length === 0 && (<div className="text-[11px] text-slate-500 dark:text-slate-400">No audience files uploaded.</div>)}
                {audiences.map(a => {
                  const audienceId = a.Id
                  const audienceName = a.Name || 'Unnamed'
                  const currentImports = (watch('IncludedFileImports') || []) as FileImportSummaryDto[]
                  const selected = currentImports.some(fi => fi.Id === audienceId)
                  return (
                    <label key={audienceId} className="flex items-start gap-2 text-[11px] cursor-pointer select-none p-1 rounded hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                      <input type="checkbox" className="mt-0.5" value={audienceId} checked={selected} onChange={() => {
                        const next: FileImportSummaryDto[] = selected
                          ? currentImports.filter(fi => fi.Id !== audienceId)
                          : [...currentImports, { Id: audienceId, Name: audienceName }]
                        setValue('IncludedFileImports', next, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
                      }} />
                      <span className="flex-1">{audienceName}</span>
                    </label>)
                })}
              </div>
              {(watch('Status') === 'Marketing' && (watch('IncludedFileImports') || []).length === 0 && !errors.IncludedFileImports) && (<div className="text-xs text-red-600 dark:text-red-400 mt-1">At least one audience is required for Marketing</div>)}
              {errors.IncludedFileImports && (<div className="text-xs text-red-600 dark:text-red-400 mt-1">{errors.IncludedFileImports.message}</div>)}
            </div>
          </div>
          <TextBoxField
            control={control}
            name="Description"
            labelSize="xs"
            label="Description"
            multiline
            rows={3}
            showCharCounter
            maxLength={5000}
            placeholder="Optional description"
            rules={{ maxLength: { value: 5000, message: 'Max 5000 characters' } }}
          />
        </form>
      )}

      <DeleteWebModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} web={web} clientCompanyId={clientCompanyId} workspaceId={workspaceId} campaignId={campaignId} onPatchWeb={onPatchWeb} />

      {showAudienceModal && (
        <CampaignListModal isOpen={showAudienceModal} onClose={() => { setShowAudienceModal(false) }} campaignId={campaignId} clientCompanyId={clientCompanyId} selectedWorkspaceId={workspaceId} onCampaignListSaved={handleAudienceCreated} />
      )}
    </div>
  )
}
